import { Day, Stop, StopStateEnum } from "@city-sage/core-client";
import { Calendar, MapPin, Clock, ChevronRight } from "lucide-react";
import { Button, Badge } from "@city-sage/aurum-avis-labs-components";
import { getDayColor } from "../../lib/constants/dayColors";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useRef, useState, useEffect } from "react";
import { useMultipleDayStops } from "../../hooks/useMultipleDayStops";
import { MarkdownRenderer } from '../MarkdownRenderer';

interface ItineraryDaysOverviewSectionProps {
  days: Day[] | undefined
  onStopClick?: (stopId: string) => void
  onGotoDayClick?: (dayId: string) => void
}

export const ItineraryDaysOverviewSection = ({ days: daysProps, onStopClick, onGotoDayClick }: ItineraryDaysOverviewSectionProps) => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Extract itineraryId from the first day (assuming all days belong to the same itinerary)
  const itineraryId = daysProps?.[0]?.itineraryId;
  
  // Fetch all stops for all days using the multiple day stops hook
  const { data: stopsData } = useMultipleDayStops({ 
    itineraryId: itineraryId || '', 
    days: daysProps,
    states: [StopStateEnum.Accepted]
  });

  const days = daysProps?.sort((a, b) => a.number - b.number) || [];
  if (!days || days.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500">
        <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">{t('daysOverview.noDaysPlanned')}</p>
      </div>
    );
  }

  // Handle scroll to update active indicator
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const cardWidth = container.children[0]?.clientWidth || 0;
      const gap = 12; // 3 * 4px (gap-3)
      const index = Math.round(scrollLeft / (cardWidth + gap));
      setActiveIndex(Math.max(0, Math.min(index, days.length - 1)));
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [days.length]);



  const scrollToCard = (index: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const cardWidth = container.children[0]?.clientWidth || 0;
    const gap = 12; // 3 * 4px (gap-3)
    const scrollLeft = index * (cardWidth + gap);
    
    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  };

  return (
    <div>
      {/* Horizontal Scrolling Cards */}
      <div className="overflow-hidden">
        <div
          ref={scrollContainerRef}
          className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide"
          style={{
            scrollSnapType: "x mandatory",
            scrollBehavior: "smooth",
          }}
        >
          {days.map((day, dayIndex) => {
            const dayColor = getDayColor(dayIndex);
            const dayStops = stopsData?.getStopsForDay(day.id) || [];
            const stopCount = dayStops.length;
            
            return (
              <motion.div
                key={day.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: dayIndex * 0.1, duration: 0.3 }}
                className="flex-shrink-0 w-full bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden cursor-pointer hover:shadow-md transition-all duration-200"
                style={{ scrollSnapAlign: "start" }}
                onClick={() => onGotoDayClick?.(day.id)}
              >
                {/* Day Header */}
                <div className={`${dayColor.bgClass} text-white px-3 py-3 relative overflow-hidden`}>
                  <div className="flex items-start justify-between">
                    <div className="space-y-2 flex-1">
                      <Badge variant="secondary" className="text-xs px-2 py-0.5">
                        Day {day.number}
                      </Badge>                        
                      <div className="font-semibold text-sm leading-tight">
                        {day.title || `Day ${day.number}`}
                      </div>

                      <div className="flex items-center gap-1 text-xs opacity-90">
                        <MapPin className="w-3 h-3" />
                        <span>{stopCount} {t('common.stops').toLowerCase()}</span>
                      </div>
                    </div>
                    
                    {/* Goto Day Button */}
                    {stopCount > 0 && onGotoDayClick && (
                      <Button
                        variant="text"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          onGotoDayClick(day.id);
                        }}
                      >
                        <ChevronRight className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Description */}
                {day.description && (
                  <div className="px-3 py-2 border-b border-gray-100">
                    <div className="text-xs text-gray-600 line-clamp-2 leading-relaxed">
                      <MarkdownRenderer content={day.description} className="text-xs text-gray-600" />
                    </div>
                  </div>
                )}

                {/* Stops Preview */}
                <div className="px-3 py-2 space-y-1 max-h-42 overflow-y-auto">
                  {stopCount === 0 ? (
                    <div className="text-center py-3 text-gray-400">
                      <div className="text-xs">{t('daysOverview.noStopsPlanned')}</div>
                    </div>
                  ) : (
                    <>
                      {/* Show first 3 stops */}
                      {dayStops.slice(0, 3).map((stop, stopIndex) => (
                        <button
                          key={stop.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            onStopClick?.(stop.id);
                          }}
                          className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors w-full text-left group"
                        >
                          <div
                            className={`w-5 h-5 rounded-full ${dayColor.bgClass} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}
                          >
                            {stopIndex + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-xs truncate leading-tight">{stop.title}</div>
                            <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                              {stop.durationMinutes && (
                                <>
                                  <Clock className="w-2 h-2" />
                                  <span>{stop.durationMinutes}m</span>
                                </>
                              )}
                              {stop.place.address?.city && (
                                <>
                                  {stop.durationMinutes && <span>•</span>}
                                  <span className="truncate">{stop.place.address.city}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <ChevronRight className="w-3 h-3 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0" />
                        </button>
                      ))}
                      
                      {/* Show "more stops" indicator if there are more than 3 */}
                      {stopCount > 3 && (
                        <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-100">
                          <div className="w-5 h-5 rounded-full bg-gray-400 flex items-center justify-center">
                            <span className="text-xs text-white font-bold">+</span>
                          </div>
                          <div className="text-xs text-gray-600 font-medium">
                            {stopCount - 3 > 1 
                              ? t('daysOverview.moreStopsPlural', { count: stopCount - 3 })
                              : t('daysOverview.moreStops', { count: stopCount - 3 })
                            }
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Scroll Indicators */}
      {days.length > 1 && (
        <div className="flex justify-center gap-2 mt-1 px-4">
          {days.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToCard(index)}
              className={`w-2 h-2 rounded-full transition-all duration-200 ${
                index === activeIndex 
                  ? 'bg-gray-600 scale-125' 
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to day ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}; 